import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@doccareplus/shared/lib/utils';

/** Vertical kebab (⋮) glyph — inline so this package stays free of an icon dependency. */
function KebabIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}

export interface ActionMenuItem {
  /** Stable key for React. */
  key: string;
  label: string;
  onSelect: () => void;
  /** Optional leading icon. */
  icon?: ReactNode;
  disabled?: boolean;
  /** Render in the danger colour (e.g. destructive actions). */
  danger?: boolean;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Accessible name for the kebab trigger (caller-translated; this package stays i18n-free). */
  triggerAriaLabel: string;
  /** Which edge the popover aligns to. Default `end` (right-aligned, for row-end placement). */
  align?: 'start' | 'end';
  className?: string;
}

const MENU_WIDTH = 180;
const MENU_MARGIN = 4;

/**
 * ActionMenu — a kebab (⋮) trigger that opens a small dropdown of row actions.
 *
 * Pure presentational: the caller supplies items and translated strings. Trigger and item
 * clicks call `stopPropagation`, so the menu can live inside a clickable table row without
 * firing the row's own click. Closes on Escape and outside pointer press.
 *
 * The popover renders through a portal at `position: fixed`, computed from the trigger's
 * bounding rect, so it escapes any scrollable/clipping ancestor (e.g. a horizontally
 * scrolling table wrapper) instead of being cut off by it.
 */
export function ActionMenu({ items, triggerAriaLabel, align = 'end', className }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const menuId = `action-menu-${reactId}`;

  const reposition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    const fitsBelow = rect.bottom + MENU_MARGIN + menuHeight <= window.innerHeight;
    const top = fitsBelow ? rect.bottom + MENU_MARGIN : Math.max(MENU_MARGIN, rect.top - MENU_MARGIN - menuHeight);
    const left =
      align === 'end'
        ? Math.max(MENU_MARGIN, rect.right - MENU_WIDTH)
        : Math.min(rect.left, window.innerWidth - MENU_WIDTH - MENU_MARGIN);
    setPosition({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onReposition = () => reposition();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const select = (item: ActionMenuItem) => {
    if (item.disabled) return;
    setOpen(false);
    item.onSelect();
  };

  return (
    <div className={cn('relative inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerAriaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((v) => !v);
        }}
        className="grid h-8 w-8 place-items-center rounded-[8px] text-[var(--text-3)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        <KebabIcon />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                setOpen(false);
              }
            }}
            style={{
              position: 'fixed',
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              width: MENU_WIDTH,
              visibility: position ? 'visible' : 'hidden',
            }}
            className="z-[1000] overflow-hidden rounded-[10px] border border-[var(--border)] bg-[var(--surface)] py-1 shadow-[var(--shadow-2)]"
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                aria-disabled={item.disabled || undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  select(item);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] font-medium transition-colors',
                  item.disabled
                    ? 'cursor-not-allowed text-[var(--text-3)] opacity-60'
                    : item.danger
                      ? 'text-[var(--danger)] hover:bg-[var(--danger-bg)]'
                      : 'text-[var(--text)] hover:bg-[var(--surface-2)]'
                )}
              >
                {item.icon && <span className="grid h-4 w-4 place-items-center text-[var(--text-3)]">{item.icon}</span>}
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
